import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentService } from '../../services/api';

/**
 * Modal para iniciar um pagamento PIX.
 */
const PaymentModal = ({ isOpen, onClose, groupId, receiverId, receiverName, suggestedAmount }) => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(suggestedAmount?.toString() || '');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Atualiza o amount quando suggestedAmount mudar ou modal abrir
  useEffect(() => {
    if (isOpen && suggestedAmount) {
      setAmount(suggestedAmount.toString());
      setError(null);
    }
  }, [isOpen, suggestedAmount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount < 0.01) {
      setError('Digite um valor válido (mínimo R$ 0,01)');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const payment = await paymentService.create({
        groupId,
        receiverId,
        amount: numericAmount,
        description: description || undefined
      });

      // Redireciona para a página de pagamento
      navigate(`/payments/${payment.id}`);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Erro ao criar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Taxa da plataforma (deve ser a mesma do backend - configurável)
  const platformFee = 0;
  const numericAmount = parseFloat(amount) || 0;
  const totalAmount = numericAmount + platformFee;

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay"
      onClick={onClose}
    >
      <div 
        className="modal-content animate-fadeIn"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary, #fff)',
          borderRadius: 16,
          maxWidth: 450,
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: 16,
          padding: 24
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 24
        }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            <i className="fa-duotone fa-qrcode" style={{ marginRight: 8 }}></i>
            Pagar com PIX
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 24,
              color: 'var(--text-muted)',
              padding: 4
            }}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Recebedor */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(52, 211, 153, 0.1))',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--success-color), #38ef7d)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 600,
            fontSize: 16
          }}>
            {receiverName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'}
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Pagando para</p>
            <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 600 }}>{receiverName}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Valor */}
          <div style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ fontWeight: 600 }}>
              Valor do Pagamento *
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ 
                position: 'absolute', 
                left: 16, 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                fontWeight: 600
              }}>
                R$
              </span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="form-input"
                placeholder="0,00"
                style={{ paddingLeft: 48 }}
                required
              />
            </div>
            {suggestedAmount && numericAmount !== suggestedAmount && (
              <button
                type="button"
                onClick={() => setAmount(suggestedAmount.toString())}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary-color)',
                  fontSize: 13,
                  marginTop: 8,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                <i className="fa-solid fa-arrow-rotate-left"></i> Usar valor sugerido ({formatCurrency(suggestedAmount)})
              </button>
            )}
          </div>

          {/* Descrição */}
          <div style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ fontWeight: 600 }}>
              Descrição (opcional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-input"
              placeholder="Ex: Acerto da conta do churrasco"
              maxLength={255}
            />
          </div>

          {/* Resumo do Valor */}
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: platformFee > 0 ? 8 : 0 }}>
              <span style={{ color: 'var(--text-muted)' }}>Valor</span>
              <span>{formatCurrency(numericAmount)}</span>
            </div>
            {platformFee > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>Taxa Dividu</span>
                <span>{formatCurrency(platformFee)}</span>
              </div>
            )}
            {platformFee > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                paddingTop: 12,
                borderTop: '1px solid var(--border-color)',
                fontWeight: 700,
                fontSize: 16
              }}>
                <span>Total</span>
                <span style={{
                  background: 'var(--gradient-primary)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(totalAmount)}
                </span>
              </div>
            )}
          </div>

          {/* Erro */}
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              color: 'var(--error-color)',
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 14
            }}>
              <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 8 }}></i>
              {error}
            </div>
          )}

          {/* Botões */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button 
              type="button" 
              onClick={onClose} 
              className="btn btn-secondary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={loading || numericAmount < 0.01}
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i> Gerando PIX...
                </>
              ) : (
                <>
                  <i className="fa-duotone fa-qrcode"></i> Gerar PIX
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
